import { Component, ChangeDetectionStrategy, ElementRef, ViewChild, inject, input, output, signal, OnDestroy } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { FileUploaderComponent, FileUploaderDialogData } from '../file-uploader/file-uploader.component';
import { UploadType } from '../../models/upload-type.type';
import { DisplayMode } from '../../models/display-mode.type';

/**
 * Standalone drop zone component for file uploads.
 * Renders a drag-and-drop UI and integrates with FileUploaderComponent dialog/bottomsheet.
 *
 * Usage:
 * ```html
 * <xmed-file-upload-drop-zone
 *   [patientId]="patient.id"
 *   [uploadType]="'patient'"
 *   (uploadComplete)="onUploadComplete($event)">
 * </xmed-file-upload-drop-zone>
 * ```
 */
@Component({
  selector: 'xmed-file-upload-drop-zone',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './file-upload-drop-zone.component.html',
  styleUrl: './file-upload-drop-zone.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUploadDropZoneComponent implements OnDestroy {
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly dialog = inject(MatDialog);

  // Inputs (same as FileUploadButtonComponent)
  patientId = input<number | null>(null);
  commentId = input<number | null>(null);
  tags = input<string[]>([]);
  uploadType = input<UploadType>('practice');
  displayMode = input<DisplayMode>('bottomsheet');
  disabled = input<boolean>(false);

  // Output
  uploadComplete = output<boolean>();

  // Internal state
  isDragging = signal(false);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private bottomSheetRef: MatBottomSheetRef<FileUploaderComponent> | null = null;
  private dialogRefInstance: MatDialogRef<FileUploaderComponent> | null = null;
  private subscription: Subscription | null = null;

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.bottomSheetRef = null;
    this.dialogRefInstance = null;
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    if (this.disabled()) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.openUploader(files);
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.openUploader(input.files);
    }
  }

  onBrowseClick(): void {
    if (!this.disabled()) {
      this.fileInput.nativeElement.click();
    }
  }

  private openUploader(files: FileList): void {
    const data: FileUploaderDialogData = {
      files: files,
      tags: this.tags(),
      type: this.uploadType(),
      patientId: this.patientId(),
      commentId: this.commentId()
    };

    if (this.displayMode() === 'dialog') {
      this.openDialog(data);
    } else {
      this.openBottomSheet(data);
    }
  }

  private openBottomSheet(data: FileUploaderDialogData): void {
    this.bottomSheetRef = this.bottomSheet.open(FileUploaderComponent, {
      disableClose: true,
      data: data
    });

    this.subscription = this.bottomSheetRef.afterDismissed().subscribe(dismissed => {
      this.fileInput.nativeElement.value = '';
      this.uploadComplete.emit(dismissed ?? false);
    });
  }

  private openDialog(data: FileUploaderDialogData): void {
    this.dialogRefInstance = this.dialog.open(FileUploaderComponent, {
      disableClose: true,
      data: data,
      width: '80vw',
      maxWidth: '900px'
    });

    this.subscription = this.dialogRefInstance.afterClosed().subscribe(result => {
      this.fileInput.nativeElement.value = '';
      this.uploadComplete.emit(result ?? false);
    });
  }
}
